import { useState } from 'react';
import { useCan, useIsAdmin } from '@/hooks/use-scopes';
import OffDaysCard from '@/components/availability/OffDaysCard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { AlertTriangle, Pencil, Save, X, Zap, Users as UsersIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import NumberInput from '@/components/ui/NumberInput';
import { api } from '@/lib/api';
import { fetchTeamCapacity, bulkCapacity, stepLabel, type CapacityStats } from '@/lib/queries';
import { bidi, BIDI_CLS } from '@/lib/bidi';
import { DESIGNATIONS } from '@shared/enums';
import { cn } from '@/lib/cn';

//: a task with no phase is named by its step, as on the Progress bar
const STAGE_NAME: Record<string, string> = { brief: 'Script', shoot: 'Shoot', raws_review: 'AD reviews raws', edit: 'Edit', dual_review: 'Final review', done: 'Client review', posted: 'Posting' };

export default function CapacityPage() {
  const allowed = useCan('tasks.read.all');
  const canEdit = useCan('users.write');
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftCap, setDraftCap] = useState<number>(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCap, setBulkCap] = useState<number>(5);
  const [bulkDesign, setBulkDesign] = useState<string>('');
  const [selectionMode, setSelectionMode] = useState(false);
  // Visible-list filter (independent of bulk action). Auto-syncs from bulkDesign
  // so picking a designation in the bulk card narrows the list below.
  const [filterDesignation, setFilterDesignation] = useState<string>('');

  const team = useQuery({ queryKey: ['team-capacity'], queryFn: fetchTeamCapacity });

  const saveCap = useMutation({
    mutationFn: async ({ id, cap }: { id: string; cap: number }) =>
      (await api.patch(`/users/${id}`, { weekly_capacity: cap })).data,
    onSuccess: () => {
      toast.success('Capacity updated');
      qc.invalidateQueries({ queryKey: ['team-capacity'] });
      qc.invalidateQueries({ queryKey: ['users'] });
      setEditingId(null);
    },
  });

  const bulk = useMutation({
    mutationFn: bulkCapacity,
    onSuccess: (r) => {
      toast.success(`Updated ${r.updated_count} user${r.updated_count === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['team-capacity'] });
      qc.invalidateQueries({ queryKey: ['users'] });
      setSelected(new Set());
    },
  });

  if (!allowed) return <Navigate to="/" replace />;

  const team_data = team.data ?? [];
  const visible = filterDesignation
    ? team_data.filter((u) => u.designations.includes(filterDesignation))
    : team_data;
  const visibleIds = new Set(visible.map((u) => u.user_id));
  const selectedInVisible = new Set(Array.from(selected).filter((id) => visibleIds.has(id)));

  // The strip totals what is on screen. Totalling the whole team while a
  // designation filter is on made the cards disagree with the list under them.
  const totals = visible.reduce(
    (acc, s) => {
      acc.cap += s.weekly_capacity;
      acc.open += s.open_tasks;
      acc.overdue += s.overdue_tasks;
      acc.done += s.completed_last_7d;
      return acc;
    },
    { cap: 0, open: 0, overdue: 0, done: 0 }
  );
  const overallUtil = totals.cap === 0 ? 0 : Math.round((totals.open / totals.cap) * 100);

  function toggleSelect(uid: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }
  function selectAll() {
    // Select only the currently-visible (filtered) users
    setSelected(new Set(visible.map((u) => u.user_id)));
  }
  function clearSelection() {
    setSelected(new Set());
  }
  function applyDesignationFilter(d: string) {
    setFilterDesignation(d);
    // Drop any selection that's no longer visible
    if (d) {
      const allowed = new Set(team_data.filter((u) => u.designations.includes(d)).map((u) => u.user_id));
      setSelected((cur) => new Set(Array.from(cur).filter((id) => allowed.has(id))));
    }
  }

  return (
    <div>
      <PageHeader
        title="Team capacity"
        subtitle="Set how much each teammate can hold concurrently. Bulk-adjust by designation or pick specific users."
      />

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard label="Total capacity"  value={totals.cap}  hint="open tasks team can hold" />
        <SummaryCard label="Open tasks now"  value={totals.open} hint={`${overallUtil}% utilized`} accent={overallUtil > 100 ? 'red' : overallUtil > 80 ? 'yellow' : 'green'} />
        <SummaryCard label="Overdue"         value={totals.overdue} hint="needs attention" accent={totals.overdue > 0 ? 'red' : 'green'} />
        <SummaryCard label="Completed (7d)"  value={totals.done}    hint="done last week" accent="green" />
      </div>

      {/* Who is away: meetings and shoots are never offered on these times */}
      {isAdmin && <OffDaysCard team />}

      {/* Bulk: by designation */}
      <Card className="mb-3">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={14} className="text-accent-400" />
          <h3 className="text-sm font-medium text-muted-strong">Bulk set by designation</h3>
        </div>
        <p className="text-[11px] text-muted mb-3">
          Apply the same capacity to everyone holding a designation. e.g. <em>"all videographers = 2"</em>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <Select
            label="Designation"
            value={bulkDesign}
            onChange={(v) => { setBulkDesign(v); applyDesignationFilter(v); }}
            placeholder="Pick a designation..."
            searchable
            options={DESIGNATIONS.map((d) => {
              const count = team_data.filter((u) => u.designations.includes(d)).length;
              return {
                value: d,
                label: d.replace(/_/g, ' '),
                hint: count > 0 ? `${count} user${count === 1 ? '' : 's'}` : undefined,
              };
            })}
          />
          <NumberInput label="Weekly capacity" value={bulkCap} onChange={setBulkCap} min={0} />
          {canEdit ? (
            <Button
              disabled={!bulkDesign}
              loading={bulk.isPending}
              onClick={() => bulk.mutate({ designation: bulkDesign, weekly_capacity: bulkCap })}
            >
              Apply to all {bulkDesign ? bulkDesign.replace(/_/g, ' ') + 's' : ''}
            </Button>
          ) : (
            <p className="text-xs text-muted/70">Changing what someone can hold is an admin edit.</p>
          )}
        </div>
      </Card>

      {/* Bulk: by selection */}
      <Card className="mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UsersIcon size={14} className="text-accent-400" />
            <h3 className="text-sm font-medium text-muted-strong">Bulk set by selection</h3>
            {selectionMode && selectedInVisible.size > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-accent-600/20 text-accent-400">
                {selectedInVisible.size} selected
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {selectionMode ? (
              <>
                <Button size="sm" variant="ghost" onClick={selectAll}>Select all visible</Button>
                <Button size="sm" variant="ghost" onClick={clearSelection}>Clear</Button>
                <Button size="sm" variant="secondary" onClick={() => { setSelectionMode(false); clearSelection(); }}>
                  Exit selection
                </Button>
              </>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setSelectionMode(true)}>
                Enable selection
              </Button>
            )}
          </div>
        </div>

        <p className="text-[11px] text-muted mb-3">
          Optional - narrow the list to one designation first, then tick the specific users
          you want to update. Leaving the filter empty lets you pick across the whole team.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end pt-2 border-t border-slate-800">
          <Select
            label="Filter list by designation"
            value={filterDesignation}
            onChange={applyDesignationFilter}
            placeholder="(no filter - show everyone)"
            searchable
            options={[
              { value: '', label: '(no filter - show everyone)' },
              ...DESIGNATIONS.map((d) => {
                const count = team_data.filter((u) => u.designations.includes(d)).length;
                return {
                  value: d,
                  label: d.replace(/_/g, ' '),
                  hint: count > 0 ? `${count} user${count === 1 ? '' : 's'}` : undefined,
                };
              }),
            ]}
          />
          {selectionMode && (
            <>
              <NumberInput label="Capacity for selected" value={bulkCap} onChange={setBulkCap} min={0} />
              <Button
                disabled={selectedInVisible.size === 0}
                loading={bulk.isPending}
                onClick={() => bulk.mutate({ user_ids: Array.from(selectedInVisible), weekly_capacity: bulkCap })}
              >
                Apply to {selectedInVisible.size} selected
              </Button>
            </>
          )}
        </div>
      </Card>

      {team.isLoading && <div className="text-muted text-sm">Loading...</div>}

      {/* Active-filter banner */}
      {filterDesignation && !team.isLoading && (
        <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg border border-accent-400/30 bg-accent-600/5 text-xs">
          <div className="text-muted-strong">
            Showing only <span className="text-accent-400 font-medium">{filterDesignation.replace(/_/g, ' ')}</span>
            <span className="text-muted"> ({visible.length} of {team_data.length} users)</span>
          </div>
          <button onClick={() => applyDesignationFilter('')} className="text-accent-400 hover:underline">
            Clear filter
          </button>
        </div>
      )}

      {!team.isLoading && visible.length === 0 && (
        <Card>
          <div className="text-center py-8 space-y-2">
            <p className="text-muted text-sm">
              {filterDesignation
                ? <>No active users hold the <span className="text-accent-400">{filterDesignation.replace(/_/g, ' ')}</span> designation.</>
                : 'No users yet.'}
            </p>
            {filterDesignation && (
              <button onClick={() => applyDesignationFilter('')} className="text-xs text-accent-400 underline">
                Clear filter
              </button>
            )}
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {visible.map((s) => {
          const editing = editingId === s.user_id;
          const isSelected = selected.has(s.user_id);
          const util = s.utilization_pct;
          return (
            <Card key={s.user_id} className={cn('space-y-3', isSelected && 'border-accent-400/60')}>
              <div className="flex items-center gap-3">
                {selectionMode && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(s.user_id)}
                    className="accent-accent-400 cursor-pointer w-4 h-4"
                  />
                )}
                {s.photo_url ? (
                  <img src={s.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-accent-600/20 text-accent-400 flex items-center justify-center text-sm font-semibold">
                    {s.display_name.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-muted-strong">{s.display_name}</div>
                  <div className="text-[11px] text-muted">
                    <span className="uppercase tracking-wide">{s.role}</span>
                    {s.designations.length > 0 && (
                      <> · {s.designations.join(', ').replace(/_/g, ' ')}</>
                    )}
                  </div>
                </div>
                <div className="text-right text-[11px] text-muted">
                  {s.completed_last_7d} done / 7d
                  {s.overdue_tasks > 0 && (
                    <div className="text-red-400 inline-flex items-center gap-1 mt-0.5">
                      <AlertTriangle size={10} /> {s.overdue_tasks} overdue
                    </div>
                  )}
                </div>
              </div>

              {/* Utilization bar */}
              <div>
                <div className="flex items-baseline justify-between mb-1.5 text-xs">
                  <div>
                    <span className="text-muted-strong font-medium">{s.open_tasks}</span>
                    <span className="text-muted"> open of </span>
                    {editing ? (
                      <span className="inline-block align-middle w-24">
                        <NumberInput value={draftCap} onChange={setDraftCap} min={0} size="sm" />
                      </span>
                    ) : (
                      <span className="text-muted-strong font-medium">{s.weekly_capacity || '-'}</span>
                    )}
                    <span className="text-muted"> capacity</span>
                  </div>
                  <div className={cn(
                    'font-mono',
                    util > 100 ? 'text-red-400' : util > 80 ? 'text-yellow-400' : 'text-emerald-400'
                  )}>
                    {s.weekly_capacity > 0 ? `${util}%` : 'no target set'}
                  </div>
                </div>
                <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                  <div
                    className={cn(
                      'h-full transition-all',
                      util > 100 ? 'bg-red-500/70' :
                      util > 80  ? 'bg-yellow-500/70' :
                                   'bg-emerald-500/60'
                    )}
                    style={{ width: `${Math.min(util, 100)}%` }}
                  />
                </div>
              </div>

              {/* Aziz 2026-09-29: the creatives behind the numbers, each with the step this
                  person holds on it; a tap opens that creative's Work card */}
              {(s.creatives ?? []).length > 0 && (
                <ul className="divide-y divide-slate-800/70 border border-slate-800 rounded-lg" data-testid="capacity-creatives">
                  {s.creatives!.map((c) => (
                    <li key={c.creative_id}>
                      <Link to={`/creatives/${c.creative_id}#work`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 hover:bg-white/[0.03]" data-testid="capacity-creative">
                        <span className="text-[11px] font-mono text-muted shrink-0">{c.code}</span>
                        <span dir="auto" className={cn('text-sm text-muted-strong truncate min-w-0 flex-1', BIDI_CLS)}>{bidi(c.title)}</span>
                        <span className="text-[11px] text-muted shrink-0">{c.clinic}</span>
                        <span className="basis-full sm:basis-auto flex flex-wrap gap-1.5">
                          {c.steps.map((st, i) => (
                            <span key={i} className={cn('text-[10px] px-1.5 py-0.5 rounded border',
                              st.overdue ? 'border-red-500/40 text-red-300 bg-red-500/10' : 'border-slate-700 text-muted-strong')}>
                              {st.phase ? stepLabel(st.stage, st.phase, c.creative_type ?? undefined) : (STAGE_NAME[st.stage] ?? st.stage.replace(/_/g, ' '))}
                              {st.status === 'submitted' ? ' · submitted' : ''}
                              {st.due_date ? ` · due ${new Date(st.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                              {st.overdue ? ' · overdue' : ''}
                            </span>
                          ))}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-800">
                <Link to={`/admin/users/${s.user_id}`}>
                  <Button size="sm" variant="secondary" icon={<ExternalLink size={12} />}>Open profile</Button>
                </Link>
                {editing ? (
                  <>
                    <Button size="sm" variant="ghost" icon={<X size={12} />} onClick={() => setEditingId(null)}>Cancel</Button>
                    <Button size="sm" icon={<Save size={12} />} onClick={() => saveCap.mutate({ id: s.user_id, cap: draftCap })} loading={saveCap.isPending}>
                      Save capacity
                    </Button>
                  </>
                ) : (
                  <Button size="sm" icon={<Pencil size={12} />} disabled={!canEdit} onClick={() => { setEditingId(s.user_id); setDraftCap(s.weekly_capacity); }}>
                    Edit capacity
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Sticky bottom action bar when selection is active */}
      {selectionMode && selectedInVisible.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-bg-card border border-accent-400/40 rounded-full shadow-card px-5 py-3 flex items-center gap-3">
          <span className="text-sm text-accent-400 font-medium">
            {selectedInVisible.size} selected
            {filterDesignation && <span className="text-muted text-xs"> ({filterDesignation.replace(/_/g, ' ')})</span>}
          </span>
          <div className="w-28">
            <NumberInput value={bulkCap} onChange={setBulkCap} min={0} size="sm" />
          </div>
          <Button
            size="sm"
            loading={bulk.isPending}
            onClick={() => bulk.mutate({ user_ids: Array.from(selectedInVisible), weekly_capacity: bulkCap })}
          >
            Apply
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSelection}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, hint, accent }: { label: string; value: number; hint?: string; accent?: 'red' | 'yellow' | 'green' }) {
  const color = accent === 'red' ? 'text-red-400' : accent === 'yellow' ? 'text-yellow-400' : accent === 'green' ? 'text-emerald-400' : 'text-accent-400';
  return (
    <Card>
      <div className="text-[10px] text-muted uppercase tracking-wider">{label}</div>
      <div className={cn('text-3xl font-semibold mt-1', color)}>{value}</div>
      {hint && <div className="text-[11px] text-muted mt-0.5">{hint}</div>}
    </Card>
  );
}
